import { Portal } from "@chakra-ui/react";
import {
  type CollisionDetection,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  type DropAnimation,
  defaultDropAnimationSideEffects,
  KeyboardSensor as LibKeyboardSensor,
  MouseSensor as LibMouseSensor,
  PointerSensor as LibPointerSensor,
  rectIntersection,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToFirstScrollableAncestor,
  restrictToWindowEdges,
} from "@dnd-kit/modifiers";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { Trans, t } from "@lingui/macro";
import { ModalConfirm } from "@src/components/ui-kit";
import type {
  DragItem,
  DropColumn,
  MovedAllocationPayload,
} from "@src/types/planning";
import { cannot } from "@src/utils/components/permissions";
import { formatDuration } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import type { Time } from "@src/utils/types";
import { AllocationItem } from "@src/widgets/AllocationItem/AllocationItem";
import { cloneDeep } from "lodash";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import {
  type FC,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  useCallback,
  useEffect,
  useTransition,
} from "react";
import { useResourcePlanningDailyViewStore } from "./timeline/ResourcePlanningDayPage";

type ResourcePlanningDndViewProps = {
  children: ReactElement;
};

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: "0.5",
      },
    },
  }),
};

const ONE_MINUTE_IN_MS = 60000;

class PointerSensor extends LibPointerSensor {
  static activators = [
    {
      eventName: "onPointerDown" as const,
      handler: ({ nativeEvent: event }: MouseEvent) => {
        return shouldHandleEvent(event.target as HTMLElement);
      },
    },
  ];
}

class MouseSensor extends LibMouseSensor {
  static activators = [
    {
      eventName: "onMouseDown" as const,
      handler: ({ nativeEvent: event }: MouseEvent) => {
        return shouldHandleEvent(event.target as HTMLElement);
      },
    },
  ];
}

class KeyboardSensor extends LibKeyboardSensor {
  static activators = [
    {
      eventName: "onKeyDown" as const,
      handler: ({ nativeEvent: event }: KeyboardEvent<Element>) => {
        return shouldHandleEvent(event.target as HTMLElement);
      },
    },
  ];
}

function shouldHandleEvent(element: HTMLElement | null) {
  let cur = element;

  while (cur) {
    if (cur.dataset?.noDnd) {
      return false;
    }
    cur = cur.parentElement;
  }

  return true;
}

export const ResourcePlanningDndView: FC<ResourcePlanningDndViewProps> =
  observer(function ResourcePlanningDndView({ children }) {
    const { UIStore } = useStore();
    const [_, startTransition] = useTransition();
    const { selectWorkTypeIfUserNoInTaskModalStore } = useStore();
    const resourcePlanningStore = useResourcePlanningDailyViewStore();
    const mouseSensor = useSensor(MouseSensor);
    const pointerSensor = useSensor(PointerSensor, {
      activationConstraint: {
        // Set `distance` to allow click actions on allocation items
        distance: 5,
      },
    });
    const touchSensor = useSensor(TouchSensor);
    const keyboardSensor = useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    });
    const sensors = useSensors(
      mouseSensor,
      touchSensor,
      keyboardSensor,
      pointerSensor,
    );

    const onDragEnd = async ({ active, activatorEvent }: DragEndEvent) => {
      const activeData = active?.data.current as DragItem | undefined;
      const payload = resourcePlanningStore.movedAllocationPayload;

      resourcePlanningStore.active = undefined;
      resourcePlanningStore.clonedData = undefined;
      resourcePlanningStore.movedAllocationPayload = undefined;

      if (!activeData || !payload) return;

      if (!payload.userItem.stats?.task_id) return;

      if (
        cannot("planning_update_all") &&
        cannot({
          do: "planning_update_own",
          eval: ({ id }) =>
            id ===
            activeData?.userItem.stats?.capacity_allocation_created_by_id,
        })
      ) {
        UIStore.toast({
          title: t`Insufficient permissions.`,
          status: "warning",
        });
        return;
      }

      const userInTask = await resourcePlanningStore.checkIfUserInTask(
        payload.to_user_id,
        payload.userItem.stats?.task_id,
      );

      if (userInTask) {
        if ("metaKey" in activatorEvent && !activatorEvent.metaKey) {
          resourcePlanningStore.moveAllocationItem(payload);
        } else {
          resourcePlanningStore.duplicateAllocationItem(payload);
        }
      } else {
        selectWorkTypeIfUserNoInTaskModalStore.modalState.onOpen({
          payload: payload,
          onCancel: () => {
            resourcePlanningStore.fetchDailyView({
              backgroundUpdate: false,
              loadForAllUsers: true,
            });
          },
          onSubmit: async (workTypeId) => {
            // It's there but package didn't type it correctly
            if ("metaKey" in activatorEvent && !activatorEvent.metaKey) {
              await resourcePlanningStore.moveAllocationItem(
                payload,
                workTypeId,
              );
            } else {
              await resourcePlanningStore.duplicateAllocationItem(
                payload,
                workTypeId,
              );
            }
          },
        });
      }
    };

    const onDragCancel = () => {
      resourcePlanningStore.active = undefined;
      resourcePlanningStore.movedAllocationPayload = undefined;
      resourcePlanningStore.revertChanges();
    };

    const onDragStart = ({ active }: DragStartEvent) => {
      const activeData = active?.data.current as DragItem | undefined;
      resourcePlanningStore.active = cloneDeep(toJS(activeData));
      resourcePlanningStore.clonedData = cloneDeep(
        toJS(resourcePlanningStore.data),
      );
    };

    // The optimistic move changes the layout, which changes collisions and fires `onDragOver` again.
    // Without this guard, items can ping-pong between positions until React throws "Maximum update depth exceeded".
    const collisionDetection: CollisionDetection = useCallback((args) => {
      if (resourcePlanningStore.isSettlingDragMove) {
        return [{ id: args.active.id }];
      }

      return rectIntersection(args);
    }, []);

    const onDragOver = useCallback(({ over, active }: DragOverEvent) => {
      startTransition(() => {
        const overData = over?.data.current as DropColumn | DragItem;
        const activeData = resourcePlanningStore.active;

        if (!overData || !activeData) return;
        // If we are dragging over the same item, do nothing
        if (over?.id === activeData.userItem.id) return;

        const isOverColumn = overData.type === "column";
        const overDay = overData.day;
        const overUser =
          "user" in overData ? overData.user : overData.userItem.user;
        const overItems = overDay?.items ?? [];
        const overIndex = overItems.findIndex(({ id }) => id === over?.id);
        const oldIndex = activeData?.day.items?.findIndex(
          ({ id }) => id === activeData.userItem.id,
        );

        if (!overDay || !overUser || oldIndex === undefined) return;
        let newIndex: number;

        if (isOverColumn && overIndex === -1) {
          newIndex = overItems.length === 0 ? 0 : overItems.length + 1;
        } else {
          const isBelowOverItem =
            over &&
            active.rect.current.translated &&
            active.rect.current.translated.top >
              over.rect.top + over.rect.height;

          const modifier = isBelowOverItem ? 1 : 0;

          newIndex =
            overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
        }

        const payload: MovedAllocationPayload = {
          from_date: new Date(activeData.day.date),
          to_date: new Date(overDay.date),
          from_user_id: activeData.userItem.user.id,
          to_user_id: overUser.id,
          userItem: activeData.userItem,
          id: activeData.userItem.id,
          oldIndex,
          newIndex,
        };

        resourcePlanningStore.updateMovedAllocationPayload(payload);
        resourcePlanningStore.optimisticallyMoveAllocationItem(payload);
      });
    }, []);

    useEffect(() => {
      const intervalId = setInterval(() => {
        resourcePlanningStore.checkIfSyncNeeded();
      }, ONE_MINUTE_IN_MS);

      return () => clearInterval(intervalId);
    }, []);

    return (
      <DndContext
        modifiers={[restrictToFirstScrollableAncestor]}
        collisionDetection={collisionDetection}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        onDragCancel={onDragCancel}
        onDragStart={onDragStart}
        sensors={sensors}
      >
        {children}
        <Portal>
          <ActiveItem />
        </Portal>
        <ModalConfirm
          isOpen={resourcePlanningStore.confirmDurationChangeModal.isOpen}
          onClose={() => {
            resourcePlanningStore.confirmDurationChangeModal.additionalData?.onCancel();
            resourcePlanningStore.confirmDurationChangeModal.onClose();
          }}
          heading={t`Confirm duration change`}
          onConfirm={() =>
            resourcePlanningStore.confirmDurationChangeModal.additionalData?.onConfirm()
          }
        >
          {typeof resourcePlanningStore.confirmDurationChangeModal
            .additionalData?.previousTime === "number" ? (
            <Trans>
              Are you sure you want to change allocation duration duration from
            </Trans>
          ) : (
            <Trans>
              Are you sure you want to change allocation end time from
            </Trans>
          )}
          &nbsp;
          <ConfirmModalDuration
            duration={
              resourcePlanningStore.confirmDurationChangeModal.additionalData
                ?.previousTime
            }
          />
          &nbsp;<Trans>to</Trans>&nbsp;
          <ConfirmModalDuration
            duration={
              resourcePlanningStore.confirmDurationChangeModal.additionalData
                ?.newTime
            }
          />
          <Trans>?</Trans>
        </ModalConfirm>
        <ModalConfirm
          isOpen={resourcePlanningStore.confirmSeparateAllocationModal.isOpen}
          onClose={() =>
            resourcePlanningStore.confirmSeparateAllocationModal.onClose()
          }
          heading={t`Confirm action`}
          onConfirm={() =>
            resourcePlanningStore.confirmSeparateAllocationModal.additionalData?.onConfirm()
          }
        >
          <Trans>
            This allocation will be disconnected from the series and will
            require separate editing.
          </Trans>
        </ModalConfirm>
      </DndContext>
    );
  });

const ActiveItem = observer(function ActiveItem() {
  const resourcePlanningStore = useResourcePlanningDailyViewStore();

  return (
    <DragOverlay
      dropAnimation={dropAnimation}
      modifiers={[restrictToWindowEdges]}
    >
      {!!resourcePlanningStore.active && (
        <AllocationItem
          shadow="xl"
          cursor="grabbing"
          userItem={resourcePlanningStore.active.userItem}
          day={resourcePlanningStore.active.day}
        />
      )}
    </DragOverlay>
  );
});
type ConfirmModalDurationProps = {
  duration: Time | number | undefined;
};
const ConfirmModalDuration = observer(function ConfirmModalDuration({
  duration,
}: ConfirmModalDurationProps) {
  if (!duration) return null;
  return typeof duration === "number" ? formatDuration(duration) : duration;
});
